import { useEffect, useState } from "react";
import { ThresholdLoaderSharedProps } from "./ThresholdLoader.props";

enum Threshold {
  Zero = "at0s",
  Two = "at2s",
  Five = "at5s",
}

/**
 * A component that conditionally displays different loading indicators
 * based on fixed time thresholds (0s, 2s, 5s).
 */
const ThresholdLoader = ({
  isLoading,
  children,
  at0s = undefined,
  at2s = undefined,
  at5s = undefined,
}: ThresholdLoaderProps) => {
  const [elapsedTime, setElapsedTime] = useState<Threshold | null>(() =>
    isLoading ? Threshold.Zero : null
  );

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    // Start at 0 seconds
    setElapsedTime(Threshold.Zero);

    // Set up timers for different thresholds
    const timer2s = setTimeout(() => {
      if (isLoading) setElapsedTime(Threshold.Two);
    }, 2000);

    const timer5s = setTimeout(() => {
      if (isLoading) setElapsedTime(Threshold.Five);
    }, 5000);

    // Clean up timers on unmount or when loading state changes
    // eslint-disable-next-line consistent-return
    return () => {
      clearTimeout(timer2s);
      clearTimeout(timer5s);
    };
  }, [isLoading]);

  // If not loading, render children
  if (!isLoading) {
    return <>{children}</>;
  }

  // Get content to display based on current threshold with fallback behavior
  const getContentToDisplay = () => {
    if (elapsedTime === Threshold.Five) {
      // If at5s is explicitly null, return null
      if (at5s === null) return null;
      // If at5s is defined, use it
      if (at5s !== undefined) return at5s;
      // Otherwise fall back to at2s
      if (at2s !== undefined) return at2s;
      // Finally fall back to at0s
      return at0s;
    }

    if (elapsedTime === Threshold.Two) {
      // If at2s is explicitly null, return null
      if (at2s === null) return null;
      // If at2s is defined, use it
      if (at2s !== undefined) return at2s;
      // Otherwise fall back to at0s
      return at0s;
    }

    // Default to at0s
    return at0s;
  };

  const content = getContentToDisplay();

  // If no content is available, return null
  if (content === undefined) {
    return null;
  }

  return <>{content}</>;
};

export default ThresholdLoader;

export type ThresholdLoaderProps = ThresholdLoaderSharedProps;
